import { Col, Row } from "antd"
import RadarChart from "./radarChart"
import BarChart from "./barChart";
import Section from "./section";
import SimpleReportTable, { SimpleReportTableColumn } from "./simpleReportTable";
import styles from '../index.module.scss';

// 雷达图数据类型定义
interface RadarData {
    data: {
        a: number;
        b: number;
        c: number;
        d: number;
        e: number;
    };
    key: string;
}

// 风险等级评估数据类型
interface RiskLevelAssessment {
    各项风险等级?: {
        [key: string]: string;
    };
    车队评分?: string;
    车队评分描述?: string;
    车队管理建议摘要?: string;
    车队管理建议明细?: ManagementAdviceItem[];
    道路安全评估?: string;
    驾驶行为评估?: string;
}

interface ManagementAdviceItem {
    维度?: string;
    风险等级?: string;
    车队得分?: number | string;
    平台均值?: number | string;
    状态?: string;
    管理建议?: string;
}

// 车辆风险数据类型定义
interface VehicleRiskData {
    comments: string;
    device_alarm_score: number;
    driver_habit_score: number;
    fleet_name: string;
    line_safe_score: number;
    mental_state_score: number;
    plate_number: string;
    safe_score: number;
    work_intensity_score: number;
    安全级别: string;
}

interface WeeklySafetyScoreProps {
    data?: RadarData[];
    riskAssessment?: RiskLevelAssessment;
    vehicleRiskTable?: VehicleRiskData[];
}

const WeeklySafetyScore = ({ data: radarData, riskAssessment, vehicleRiskTable }: WeeklySafetyScoreProps) => {

    // 检查数据是否存在并且有效
    const hasValidData = radarData && Array.isArray(radarData) && radarData.length > 0;

    // 转换雷达图数据格式
    const transformRadarData = () => {
        if (!hasValidData) return undefined;

        return radarData.map(item => {
            // 根据key决定显示的名称
            const name = item.key === 'platform' ? '平台数据' : '车队数据';

            // 将数据对象转换为数组格式：[a, b, c, d, e]
            const value = [
                item.data.a,
                item.data.b,
                item.data.c,
                item.data.d,
                item.data.e
            ];

            return {
                name,
                value
            };
        });
    };

    // 生成柱状图数据：基于车辆风险表统计
    const generateBarData = () => {
        // 优先使用车辆风险表数据
        if (vehicleRiskTable && Array.isArray(vehicleRiskTable) && vehicleRiskTable.length > 0) {
            // 统计各个安全级别的车辆数量
            const riskCounts: { [key: string]: number } = {};

            vehicleRiskTable.forEach(vehicle => {
                const level = vehicle.安全级别;
                if (level) {
                    riskCounts[level] = (riskCounts[level] || 0) + 1;
                }
            });

            // 转换为柱状图所需的数据格式
            const barValue = Object.entries(riskCounts).map(([level, count]) => [level, count] as [string, number]);

            return [
                {
                    name: '车辆安全级别分布',
                    value: barValue,
                },
            ];
        }

        // 如果没有车辆风险表数据，回退到原来的逻辑
        if (!riskAssessment?.各项风险等级) {
            // 如果没有数据，返回默认空数据
            return [
                {
                    name: '安全级别统计',
                    value: [] as Array<[string, number]>,
                },
            ];
        }

        // 统计各个风险等级的数量
        const riskCounts: { [key: string]: number } = {};
        const riskLevels = riskAssessment.各项风险等级;

        // 遍历所有风险等级项，统计每种风险等级的数量
        Object.values(riskLevels).forEach(level => {
            if (level) {
                riskCounts[level] = (riskCounts[level] || 0) + 1;
            }
        });

        // 转换为柱状图所需的数据格式
        const barValue = Object.entries(riskCounts).map(([level, count]) => [level, count] as [string, number]);

        return [
            {
                name: '安全级别统计',
                value: barValue,
            },
        ];
    };

    // 判断是否有柱状图数据
    const hasBarData = (vehicleRiskTable && vehicleRiskTable.length > 0) || riskAssessment?.各项风险等级;

    // 如果既没有雷达图数据，也没有风险评估数据和车辆风险表数据，则不渲染组件
    if (!hasValidData && !riskAssessment && !vehicleRiskTable) {
        return null;
    }

    // 获取转换后的雷达图数据
    const transformedData = transformRadarData();

    // 获取动态生成的柱状图数据
    const barData = generateBarData();

    const adviceDetails = Array.isArray(riskAssessment?.车队管理建议明细)
        ? riskAssessment?.车队管理建议明细 || []
        : [];

    const getStatusClassName = (status?: string) => {
        if (status === '需关注') return `${styles.statusTag} ${styles.statusAttention}`;
        if (status === '保持') return `${styles.statusTag} ${styles.statusKeep}`;
        return `${styles.statusTag} ${styles.statusPending}`;
    };

    const adviceColumns: SimpleReportTableColumn<ManagementAdviceItem>[] = [
        { title: '维度', dataIndex: '维度', key: 'dimension', width: 100 },
        { title: '风险等级', dataIndex: '风险等级', key: 'riskLevel', width: 100 },
        { title: '车队得分', dataIndex: '车队得分', key: 'fleetScore', width: 100 },
        { title: '平台均值', dataIndex: '平台均值', key: 'platformAverage', width: 100 },
        {
            title: '状态',
            dataIndex: '状态',
            key: 'status',
            width: 100,
            render: (value: string) => <span className={getStatusClassName(value)}>{value || '待补充'}</span>
        },
        { title: '管理建议', dataIndex: '管理建议', key: 'managementAdvice' },
    ];

    return (
        <Section title="二、本周车队安全评分及管理建议">
            <div>
                <Row>
                    {hasValidData && (
                        <Col span={hasBarData ? 12 : 24}>
                            <RadarChart
                                title="车队风险雷达图"
                                data={transformedData}
                            />
                        </Col>
                    )}
                    {hasBarData && (
                        <Col span={hasValidData ? 12 : 24}>
                            <BarChart
                                maxBarWidth="20%"
                                title="车队安全评分分布"
                                data={barData}
                                yAxisLabel={'车辆数量'}
                                yAxisIntegerOnly={true}
                            />
                        </Col>
                    )}
                </Row>
                <Row>
                    <Col span={24}>
                        <h3>管理建议</h3>
                        {riskAssessment?.车队评分 && (
                            <p>{riskAssessment.车队评分}</p>
                        )}
                        {riskAssessment?.车队评分描述 && (
                            <p>{riskAssessment.车队评分描述}</p>
                        )}
                        {riskAssessment?.车队管理建议摘要 && (
                            <p>{riskAssessment.车队管理建议摘要}</p>
                        )}
                        <SimpleReportTable
                            columns={adviceColumns}
                            dataSource={adviceDetails}
                            rowKey={(record, index) => record.维度 || index}
                            scrollX
                            emptyText="暂无管理建议明细"
                        />
                    </Col>
                </Row>
            </div>
        </Section>
    )
}

export default WeeklySafetyScore
